import { ACL_PRIVATE, MAX_KEYS } from '../../s3';
import {
    BUCKET_OBJECTS_ACL_CANCEL,
    BUCKET_OBJECTS_ACL_CHANGE_FAILURE,
    BUCKET_OBJECTS_ACL_CHANGE_REQUEST,
    BUCKET_OBJECTS_ACL_CHANGE_SUCCESS,
    BUCKET_OBJECTS_ACL_FETCH_FAILURE,
    BUCKET_OBJECTS_ACL_FETCH_REQUEST,
    BUCKET_OBJECTS_ACL_FETCH_SUCCESS,
} from '../actions/acl';
import {
    BUCKET_FOLDER_CREATE_CANCEL,
    BUCKET_FOLDER_CREATE_FAILURE,
    BUCKET_FOLDER_CREATE_REQUEST,
    BUCKET_FOLDER_CREATE_START,
    BUCKET_FOLDER_CREATE_SUCCESS,
} from '../actions/folder';
import {
    BUCKET_OBJECTS_LIST_ADD,
    BUCKET_OBJECTS_LIST_FAILURE,
    BUCKET_OBJECTS_LIST_REQUEST,
    BUCKET_OBJECTS_LIST_SUCCESS,
} from '../actions/list';
import {
    BUCKET_OBJECTS_MULTI_ACL_CHANGE_CANCEL,
    BUCKET_OBJECTS_MULTI_ACL_CHANGE_FAILURE,
    BUCKET_OBJECTS_MULTI_ACL_CHANGE_REQUEST,
    BUCKET_OBJECTS_MULTI_ACL_CHANGE_START,
    BUCKET_OBJECTS_MULTI_ACL_CHANGE_SUCCESS,
} from '../actions/multiAcl';
import {
    BUCKET_OBJECTS_REMOVE_CANCEL,
    BUCKET_OBJECTS_REMOVE_FAILURE,
    BUCKET_OBJECTS_REMOVE_REQUEST,
    BUCKET_OBJECTS_REMOVE_START,
    BUCKET_OBJECTS_REMOVE_SUCCESS,
} from '../actions/remove';
import {
    BUCKET_OBJECTS_TOGGLE_SELECTION,
    BUCKET_OBJECTS_TOGGLE_SELECTION_ALL,
} from '../actions/select';
import { BUCKET_OBJECTS_SETUP } from '../actions/setup';
import { BUCKET_OBJECTS_SORT } from '../actions/sort';
import {
    BUCKET_OBJECTS_UPLOAD_CANCEL,
    BUCKET_OBJECTS_UPLOAD_CHANGE_ACL,
    BUCKET_OBJECTS_UPLOAD_START,
} from '../actions/upload';
import getSelected from '../selectors/getSelected';
import { SORT_NAME_ASC } from '../utils/sort';

const initialListState = {
    CommonPrefixes: [],
    Contents: [],
    selection: {},
    listLoading: false,
    listLoaded: false,
    listError: false,
};

const initialRemoveState = {
    removePrefixes: [],
    removeKeys: [],
    removeOpen: false,
    removeLoading: false,
};

const initialAclState = {
    aclKey: '',
    aclType: '',
    aclOpen: false,
    aclLoading: false,
};

const initialMultiAclState = {
    multiAclPrefixes: [],
    multiAclKeys: [],
    multiAclOpen: false,
    multiAclLoading: false,
};

const initialFolderState = {
    folderOpen: false,
    folderLoading: false,
    folderError: '',
};

const initialState = {
    Bucket: '',
    Prefix: '',
    MaxKeys: MAX_KEYS,
    ContinuationToken: '',
    tokens: [],
    ...initialListState,
    ...initialRemoveState,
    ...initialAclState,
    ...initialMultiAclState,
    ...initialFolderState,
    uploadOpen: false,
    uploadAcl: ACL_PRIVATE,
    sortType: SORT_NAME_ASC,
};

// eslint-disable-next-line
export default function storageBucketObjects(state, { type, data }) {
    if (!state) {
        return initialState;
    }

    switch (type) {
        case BUCKET_OBJECTS_SETUP: {
            let { Bucket, Prefix, MaxKeys, ContinuationToken } = data;
            let { tokens } = state;

            if (
                ContinuationToken &&
                Bucket === state.Bucket &&
                Prefix === state.Prefix
            ) {
                const index = tokens.indexOf(ContinuationToken);

                if (index === -1) {
                    tokens = [ContinuationToken];
                } else {
                    tokens = tokens.slice(0, index + 1);
                }
            } else {
                tokens = [];
            }

            if (MaxKeys) {
                MaxKeys = Math.max(1, Math.min(MAX_KEYS, MaxKeys));
            } else {
                MaxKeys = state.MaxKeys;
            }

            return {
                ...state,
                ...initialListState,
                Bucket,
                Prefix,
                MaxKeys,
                ContinuationToken,
                tokens,
            };
        }

        case BUCKET_OBJECTS_LIST_REQUEST: {
            return {
                ...state,
                ...initialListState,
                listLoading: true,
                listLoaded: false,
            };
        }

        case BUCKET_OBJECTS_LIST_ADD: {
            const { CommonPrefix, Content } = data;
            const prefixes = [...state.CommonPrefixes];
            const contents = [...state.Contents];

            if (CommonPrefix) {
                const item = prefixes.find(
                    ({ Prefix }) => Prefix === CommonPrefix.Prefix,
                );

                if (!item) {
                    prefixes.unshift(CommonPrefix);
                }
            } else if (Content) {
                const item = contents.find(({ Key }) => Key === Content.Key);

                if (!item) {
                    contents.unshift(Content);
                }
            }

            return {
                ...state,
                ...initialListState,
                CommonPrefixes: prefixes,
                Contents: contents,
            };
        }

        case BUCKET_OBJECTS_LIST_SUCCESS: {
            let { Prefix, tokens } = state;
            const { NextContinuationToken, CommonPrefixes, Contents } = data;
            const item = Contents[0];

            if (item && item.Key === Prefix && item.Size === 0) {
                Contents.shift();
            }

            tokens = [...tokens];

            if (NextContinuationToken) {
                tokens.push(NextContinuationToken);
            }

            return {
                ...state,
                ...initialListState,
                tokens,
                CommonPrefixes,
                Contents,
                listLoaded: true,
            };
        }

        case BUCKET_OBJECTS_LIST_FAILURE: {
            return {
                ...state,
                ...initialListState,
                listLoaded: false,
                listError: true,
            };
        }

        case BUCKET_OBJECTS_TOGGLE_SELECTION: {
            const { id } = data;
            const selection = { ...state.selection };

            selection[id] = !selection[id];

            return {
                ...state,
                selection,
            };
        }

        case BUCKET_OBJECTS_TOGGLE_SELECTION_ALL: {
            const { CommonPrefixes, Contents } = state;
            const { prefixes, contents } = getSelected(state);
            const selected =
                CommonPrefixes.length + Contents.length !==
                prefixes.length + contents.length;
            const selection = {};

            for (const { Prefix } of CommonPrefixes) {
                selection[Prefix] = selected;
            }

            for (const { Key } of Contents) {
                selection[Key] = selected;
            }

            return {
                ...state,
                selection,
            };
        }

        case BUCKET_OBJECTS_REMOVE_START: {
            return {
                ...state,
                ...initialRemoveState,
                removePrefixes: data.prefixes,
                removeKeys: data.keys,
                removeOpen: true,
            };
        }

        case BUCKET_OBJECTS_REMOVE_CANCEL: {
            return {
                ...state,
                ...initialRemoveState,
                removeOpen: false,
            };
        }

        case BUCKET_OBJECTS_REMOVE_REQUEST: {
            return {
                ...state,
                removeLoading: true,
            };
        }

        case BUCKET_OBJECTS_REMOVE_SUCCESS:
        case BUCKET_OBJECTS_REMOVE_FAILURE: {
            const { prefixes, keys } = data;
            let CommonPrefixes = [...state.CommonPrefixes];
            let Contents = [...state.Contents];

            if (Object.keys(prefixes).length) {
                CommonPrefixes = CommonPrefixes.filter(
                    ({ Prefix }) => !prefixes[Prefix],
                );
            }

            if (Object.keys(keys).length) {
                Contents = Contents.filter(({ Key }) => !keys[Key]);
            }

            return {
                ...state,
                ...initialRemoveState,
                CommonPrefixes,
                Contents,
                selection: {},
            };
        }

        case BUCKET_OBJECTS_ACL_CANCEL: {
            return {
                ...state,
                ...initialAclState,
            };
        }

        case BUCKET_OBJECTS_ACL_FETCH_REQUEST: {
            return {
                ...state,
                ...initialAclState,
                aclKey: data.Key,
                aclOpen: true,
                aclLoading: true,
            };
        }

        case BUCKET_OBJECTS_ACL_FETCH_SUCCESS:
        case BUCKET_OBJECTS_ACL_CHANGE_SUCCESS: {
            return {
                ...state,
                aclType: data.type,
                aclLoading: false,
            };
        }

        case BUCKET_OBJECTS_ACL_FETCH_FAILURE:
        case BUCKET_OBJECTS_ACL_CHANGE_FAILURE: {
            return {
                ...state,
                aclLoading: false,
            };
        }

        case BUCKET_OBJECTS_ACL_CHANGE_REQUEST: {
            return {
                ...state,
                aclLoading: true,
            };
        }

        case BUCKET_OBJECTS_MULTI_ACL_CHANGE_START: {
            return {
                ...state,
                multiAclPrefixes: data.prefixes,
                multiAclKeys: data.keys,
                multiAclOpen: true,
            };
        }

        case BUCKET_OBJECTS_MULTI_ACL_CHANGE_REQUEST: {
            return {
                ...state,
                multiAclLoading: true,
            };
        }

        case BUCKET_OBJECTS_MULTI_ACL_CHANGE_SUCCESS:
        case BUCKET_OBJECTS_MULTI_ACL_CHANGE_FAILURE:
        case BUCKET_OBJECTS_MULTI_ACL_CHANGE_CANCEL: {
            return {
                ...state,
                ...initialMultiAclState,
            };
        }

        case BUCKET_OBJECTS_UPLOAD_START: {
            return {
                ...state,
                uploadOpen: true,
            };
        }

        case BUCKET_OBJECTS_UPLOAD_CANCEL: {
            return {
                ...state,
                uploadOpen: false,
            };
        }

        case BUCKET_OBJECTS_UPLOAD_CHANGE_ACL: {
            return {
                ...state,
                uploadAcl: data.ACL,
            };
        }

        case BUCKET_FOLDER_CREATE_START: {
            return {
                ...state,
                folderOpen: true,
            };
        }

        case BUCKET_FOLDER_CREATE_CANCEL:
        case BUCKET_FOLDER_CREATE_SUCCESS: {
            return {
                ...state,
                ...initialFolderState,
            };
        }

        case BUCKET_FOLDER_CREATE_REQUEST: {
            return {
                ...state,
                folderLoading: true,
                folderError: '',
            };
        }

        case BUCKET_FOLDER_CREATE_FAILURE: {
            return {
                ...state,
                folderLoading: false,
                folderError: data.error,
            };
        }

        case BUCKET_OBJECTS_SORT: {
            return {
                ...state,
                sortType: data.type,
            };
        }

        default:
            return state;
    }
}
